if (!customElements.get("pm-plan-days")) {
  customElements.define(
    "pm-plan-days",
    class extends HTMLElement {
      connectedCallback() {
        if (this.navigation) return;
        this.days = Array.from(this.querySelectorAll(".pm-wkp__day"));
        if (this.days.length < 2) return;
        this.media = window.matchMedia("(max-width: 749px)");
        this.selectedDay = this.selectedDay ?? 0;
        this.navigation = document.createElement("nav");
        this.navigation.className = "pm-wkp__day-controls";
        this.navigation.setAttribute("aria-label", this.dataset.label);
        this.buttons = this.days.map((day, index) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "pm-wkp__day-button";
          button.textContent = day
            .querySelector(".pm-wkp__day-hd")
            .textContent.trim();
          return { button, index };
        });
        const all = document.createElement("button");
        all.type = "button";
        all.className = "pm-wkp__full-week";
        all.textContent = this.dataset.all;
        this.buttons.push({ button: all, index: -1 });
        this.buttons.forEach((entry) => {
          entry.select = () => {
            this.selectedDay = entry.index;
            this.updateDays();
          };
          entry.button.addEventListener("click", entry.select);
          this.navigation.append(entry.button);
        });
        this.onBreakpoint = () => this.updateDays();
        this.media.addEventListener("change", this.onBreakpoint);
        this.prepend(this.navigation);
        this.updateDays();
      }

      updateDays() {
        this.navigation.hidden = !this.media.matches;
        this.days.forEach((day, index) => {
          const hidden =
            this.media.matches &&
            this.selectedDay !== -1 &&
            index !== this.selectedDay;
          if (hidden && day.contains(document.activeElement)) {
            this.buttons[this.selectedDay].button.focus();
          }
          day.hidden = hidden;
        });
        this.buttons.forEach(({ button, index }) =>
          button.setAttribute(
            "aria-pressed",
            String(index === this.selectedDay),
          ),
        );
      }

      disconnectedCallback() {
        if (!this.navigation) return;
        this.media.removeEventListener("change", this.onBreakpoint);
        this.buttons.forEach(({ button, select }) =>
          button.removeEventListener("click", select),
        );
        this.days.forEach((day) => {
          day.hidden = false;
        });
        this.navigation.remove();
        this.navigation = null;
        this.days = this.buttons = this.media = this.onBreakpoint = null;
      }
    },
  );
}

if (!customElements.get("pm-weekly-plan-controller")) {
  customElements.define(
    "pm-weekly-plan-controller",
    class extends HTMLElement {
      connectedCallback() {
        if (this.root) return;
        const root = this.closest(".pm-wkp-section");
        if (!root) return;
        this.root = root;
        // One parse per connected section, never per meal or option change.
        this.variants = Object.create(null);
        try {
          const registry = JSON.parse(
            this.querySelector("[data-plan-variants]").textContent,
          );
          if (
            registry &&
            typeof registry === "object" &&
            !Array.isArray(registry)
          ) {
            Object.entries(registry).forEach(([key, variants]) => {
              if (!Array.isArray(variants)) return;
              this.variants[key] = variants
                .filter((variant) => variant && variant.id != null)
                .map(({ id, title, option1, option2, available }) => ({
                  id,
                  title,
                  option1,
                  option2,
                  available,
                }));
            });
          }
        } catch {
          // Keep Liquid-rendered meal links if enhancement data is absent/malformed.
        }
        this.buttons = Array.from(root.querySelectorAll(".pm-wkp__var-btn"));
        this.onVariantClick = (event) =>
          this.selectVariant(event.currentTarget);
        this.buttons.forEach((button) =>
          button.addEventListener("click", this.onVariantClick),
        );
        const selected = root.querySelector(".pm-wkp__var-btn--sel");
        if (selected) this.updateMealLinks(selected.dataset.variantTitle);
      }

      disconnectedCallback() {
        this.buttons?.forEach((button) =>
          button.removeEventListener("click", this.onVariantClick),
        );
        this.buttons = null;
        this.onVariantClick = null;
        this.variants = null;
        this.root = null;
      }

      selectVariant(button) {
        this.buttons.forEach((other) => {
          other.classList.toggle("pm-wkp__var-btn--sel", other === button);
          other.setAttribute("aria-pressed", String(other === button));
        });
        const input = this.root.querySelector("[data-plan-variant-id]");
        const price = this.root.querySelector("[data-plan-price]");
        const compare = this.root.querySelector("[data-plan-compare]");
        const sale = this.root.querySelector(
          '.pm-wkp__price-wrap [id^="NoMediaStandardBadge-"]',
        );
        const action = this.root.querySelector("[data-plan-action]");
        if (input) input.value = button.dataset.variantId;
        if (price) price.textContent = button.dataset.price;
        if (compare) {
          compare.textContent = button.dataset.compare || "";
          compare.hidden = !button.dataset.compare;
        }
        if (sale) {
          sale.textContent = button.dataset.saleText || "";
          sale.hidden = !button.dataset.saleText;
        }
        if (action) {
          action.disabled = button.disabled;
          action.textContent = button.disabled
            ? "Nije dostupno"
            : "Dodaj u košaricu";
        }
        this.updateMealLinks(button.dataset.variantTitle);
      }

      updateMealLinks(size) {
        if (!size) return;
        this.root
          .querySelectorAll(".pm-wkp__meal[data-meal-product-key]")
          .forEach((meal) => {
            const variants = this.variants[meal.dataset.mealProductKey];
            if (!Array.isArray(variants)) return;
            // SnackMe: flavor is option1, size option2. Other meals reverse them.
            const snack = !!meal.dataset.snackme;
            const sizeKey = snack ? "option2" : "option1";
            const flavorKey = snack ? "option1" : "option2";
            const flavor =
              (snack ? meal.dataset.mealOpt1 : meal.dataset.mealOpt2) || "";
            const fitsSize = (variant) =>
              (variant[sizeKey] || "") === size && variant.available !== false;
            const match =
              variants.find(
                (variant) =>
                  fitsSize(variant) && (variant[flavorKey] || "") === flavor,
              ) || variants.find(fitsSize);
            if (!match) return;
            meal.href =
              (meal.dataset.mealProductUrl || "") + "?variant=" + match.id;
            let label = meal.querySelector(".pm-wkp__meal-variant");
            const title = match.title || "";
            if (title && title !== "Default Title") {
              if (!label) {
                const info = meal.querySelector(".pm-wkp__meal-info");
                if (!info) return;
                label = document.createElement("div");
                label.className = "pm-wkp__meal-variant";
                info.appendChild(label);
              }
              label.textContent = title;
              label.style.display = "";
            } else if (label) label.style.display = "none";
          });
      }
    },
  );
}
